import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { BASE_URL } from '../../constants/apis';
import { sanitizeMobile } from '../../utils/phone';
import { getProformaInvoiceForEdit, patchProformaInvoice } from '../../services/labelInvoice';
import { ProductSearchInput } from './LabelInvoiceGenerator';

const Spinner = ({ className = 'h-4 w-4 border-white' }) => (
    <div className={`animate-spin rounded-full border-b-2 ${className}`} />
);

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const EMPTY_ITEM = { product_name: '', hsn_code: '', quantity: 1, rate: '', gst_percent: 0, is_include: true };

function toDateInputValue(raw) {
    if (raw == null || raw === '') return '';
    const s = String(raw);
    if (DATE_INPUT_PATTERN.test(s)) return s;
    const d = s.split('T')[0];
    return DATE_INPUT_PATTERN.test(d) ? d : '';
}

function extractPayload(res) {
    const d = res?.data;
    if (!d) return null;
    if (d.id) return d;
    if (d.data && d.data.id) return d.data;
    return null;
}

function resolvePdfUrl(url) {
    if (!url) return '';
    return url.startsWith('http') ? url : `${BASE_URL}${url}`;
}

function normalizeItemRow(i) {
    return {
        product_name: (i?.product_name ?? '').trim(),
        hsn_code: (i?.hsn_code ?? '').trim(),
        quantity: Number(i?.quantity) || 0,
        rate: Number(i?.rate) || 0,
        gst_percent: Number(i?.gst_percent) || 0,
        is_include: i?.is_include !== false,
    };
}

function itemsSignature(items) {
    return JSON.stringify((items || []).map(normalizeItemRow));
}

function mapApiItemsToForm(items) {
    if (!Array.isArray(items) || items.length === 0) return [{ ...EMPTY_ITEM }];
    return items.map((i) => ({
        product_name: i.product_name ?? '',
        hsn_code: i.hsn_code ?? '',
        quantity: i.quantity != null ? i.quantity : 1,
        rate: i.rate != null ? i.rate : '',
        gst_percent: i.gst_percent != null ? i.gst_percent : 0,
        is_include: i.is_include !== false,
    }));
}

function buildFormFromApi(row) {
    return {
        status: row.status === 'FINAL' ? 'FINAL' : 'DRAFT',
        proforma_date: toDateInputValue(row.proforma_date),
        customer_name: row.customer_name ?? '',
        business_name: row.business_name ?? '',
        customer_phone: row.customer_phone ?? '',
        customer_alternate_phone: row.customer_alternate_phone ?? '',
        customer_address: row.customer_address ?? '',
        customer_gst: row.customer_gst ?? '',
        discount_amount: row.discount_amount != null && row.discount_amount !== '' ? String(row.discount_amount) : '',
        items: mapApiItemsToForm(row.items),
    };
}

function buildPatchBody(proformaId, initialForm, currentForm, isStandalone) {
    const body = { proforma_id: proformaId };
    if (initialForm.status !== currentForm.status) body.status = currentForm.status;
    if (initialForm.proforma_date !== currentForm.proforma_date) body.proforma_date = currentForm.proforma_date;
    if (initialForm.customer_name.trim() !== currentForm.customer_name.trim()) {
        body.customer_name = currentForm.customer_name.trim();
    }
    if (initialForm.business_name.trim() !== currentForm.business_name.trim()) {
        body.business_name = currentForm.business_name.trim();
    }
    if (initialForm.customer_phone.trim() !== currentForm.customer_phone.trim()) {
        body.customer_phone = currentForm.customer_phone.trim();
    }
    if (initialForm.customer_alternate_phone.trim() !== currentForm.customer_alternate_phone.trim()) {
        body.customer_alternate_phone = currentForm.customer_alternate_phone.trim();
    }
    if (initialForm.customer_address.trim() !== currentForm.customer_address.trim()) {
        body.customer_address = currentForm.customer_address.trim();
    }
    if (initialForm.customer_gst.trim() !== currentForm.customer_gst.trim()) {
        body.customer_gst = currentForm.customer_gst.trim();
    }
    if (isStandalone) {
        const di = Number(initialForm.discount_amount) || 0;
        const dc = Number(currentForm.discount_amount) || 0;
        if (di !== dc) body.discount_amount = dc;
        if (itemsSignature(initialForm.items) !== itemsSignature(currentForm.items)) {
            body.items = currentForm.items
                .filter((i) => i.product_name.trim())
                .map((i) => {
                    const line = {
                        product_name: i.product_name.trim(),
                        quantity: Number(i.quantity) || 1,
                        rate: Number(i.rate) || 0,
                        is_include: i.is_include !== false,
                    };
                    const gst = Number(i.gst_percent);
                    if (gst) line.gst_percent = gst;
                    const hsn = (i.hsn_code || '').trim();
                    if (hsn) line.hsn_code = hsn;
                    return line;
                });
        }
    }
    const keys = Object.keys(body);
    if (keys.length === 1 && keys[0] === 'proforma_id') return null;
    return body;
}

function mergeProformaResponseIntoForm(prev, apiRow) {
    if (!apiRow) return prev;
    const next = { ...prev };
    if (apiRow.status != null) next.status = apiRow.status === 'FINAL' ? 'FINAL' : 'DRAFT';
    if (apiRow.proforma_date != null) next.proforma_date = toDateInputValue(apiRow.proforma_date);
    if (apiRow.customer_name != null) next.customer_name = apiRow.customer_name;
    if (apiRow.business_name != null) next.business_name = apiRow.business_name;
    if (apiRow.customer_phone != null) next.customer_phone = apiRow.customer_phone;
    if (apiRow.customer_alternate_phone != null) next.customer_alternate_phone = apiRow.customer_alternate_phone;
    if (apiRow.customer_address != null) next.customer_address = apiRow.customer_address;
    if (apiRow.customer_gst != null) next.customer_gst = apiRow.customer_gst;
    if (apiRow.discount_amount != null) next.discount_amount = String(apiRow.discount_amount);
    if (Array.isArray(apiRow.items)) next.items = mapApiItemsToForm(apiRow.items);
    return next;
}

function calcItemTaxable(item) {
    const base = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
    if (item.is_include === false) {
        return base;
    } else {
        const gst = Number(item.gst_percent) || 0;
        return base - (base * gst / (100 + gst));
    }
}

function calcItemGst(item) {
    const base = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
    const gst = Number(item.gst_percent) || 0;
    if (!gst) return 0;
    if (item.is_include === false) {
        return (base * gst) / 100;
    } else {
        return (base * gst) / (100 + gst);
    }
}

function calcItemTotal(item) {
    const base = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
    if (item.is_include === false) {
        const gst = Number(item.gst_percent) || 0;
        return base + (base * gst / 100);
    } else {
        return base;
    }
}

function ProformaInvoiceEdit() {
    const { proformaId } = useParams();
    const navigate = useNavigate();

    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [refreshingCustomer, setRefreshingCustomer] = useState(false);

    const [meta, setMeta] = useState({ id: null, order_id: null, proforma_number: null, pdf_url: '' });
    const [form, setForm] = useState(() => ({
        status: 'DRAFT',
        proforma_date: '',
        customer_name: '',
        business_name: '',
        customer_phone: '',
        customer_alternate_phone: '',
        customer_address: '',
        customer_gst: '',
        discount_amount: '',
        items: [{ ...EMPTY_ITEM }],
    }));
    const [initialForm, setInitialForm] = useState(null);

    const isStandalone = meta.order_id == null || meta.order_id === '';

    const load = useCallback(async () => {
        if (!proformaId) {
            setLoadError('Missing proforma id');
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const res = await getProformaInvoiceForEdit(proformaId);
            const row = extractPayload(res);
            if (!row?.id) {
                setLoadError('Proforma invoice not found');
                setLoading(false);
                return;
            }
            const f = buildFormFromApi(row);
            setForm(f);
            setInitialForm(JSON.parse(JSON.stringify(f)));
            setMeta({
                id: row.id,
                order_id: row.order_id,
                proforma_number: row.proforma_number ?? '',
                pdf_url: row.pdf_url ?? '',
            });
        } catch (e) {
            console.error(e);
            setLoadError(e?.response?.data?.message || e?.message || 'Failed to load proforma invoice');
        } finally {
            setLoading(false);
        }
    }, [proformaId]);

    useEffect(() => {
        load();
    }, [load]);

    const pdfHref = useMemo(() => resolvePdfUrl(meta.pdf_url), [meta.pdf_url]);

    const handleField = (key, value) => {
        if (key === 'customer_phone' || key === 'customer_alternate_phone') {
            value = sanitizeMobile(value);
        }
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setForm((prev) => {
            const items = [...prev.items];
            items[index] = { ...items[index], [field]: value };
            return { ...prev, items };
        });
    };

    const handleProductSelect = (index, selected) => {
        setForm((prev) => {
            const items = [...prev.items];
            items[index] = { ...items[index], product_name: selected.product_name, rate: selected.rate };
            return { ...prev, items };
        });
    };

    const addItem = () => {
        const currentIsInclude = form.items[0]?.is_include !== false;
        setForm((prev) => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM, is_include: currentIsInclude }] }));
    };

    const removeItem = (index) => {
        setForm((prev) => {
            if (prev.items.length <= 1) {
                toast.error('At least one line item is required');
                return prev;
            }
            return { ...prev, items: prev.items.filter((_, i) => i !== index) };
        });
    };

    const validate = () => {
        if (!form.customer_name.trim()) {
            toast.error('Customer name is required');
            return false;
        }
        if (!form.proforma_date || !DATE_INPUT_PATTERN.test(form.proforma_date)) {
            toast.error('Proforma date must be YYYY-MM-DD');
            return false;
        }
        if (isStandalone) {
            const ok = form.items.some((i) => i.product_name.trim() && Number(i.rate) > 0);
            if (!ok) {
                toast.error('Add at least one item with product name and rate');
                return false;
            }
        }
        return true;
    };

    const applyServerRow = (row) => {
        if (!row) return;
        setMeta((m) => ({
            ...m,
            pdf_url: row.pdf_url != null ? row.pdf_url : m.pdf_url,
            proforma_number: row.proforma_number != null ? row.proforma_number : m.proforma_number,
        }));
        setForm((prev) => {
            const merged = mergeProformaResponseIntoForm(prev, row);
            setInitialForm(JSON.parse(JSON.stringify(merged)));
            return merged;
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!initialForm || !validate()) return;
        const body = buildPatchBody(proformaId, initialForm, form, isStandalone);
        if (!body) {
            toast.info('No changes to save');
            return;
        }
        setSaving(true);
        try {
            const res = await patchProformaInvoice(body);
            const row = extractPayload(res) || res?.data;
            toast.success(res?.data?.message || 'Proforma invoice updated');
            if (row?.pdf_url != null) {
                setMeta((m) => ({ ...m, pdf_url: row.pdf_url }));
            }
            setForm((prev) => {
                const merged = row && typeof row === 'object' ? mergeProformaResponseIntoForm(prev, row) : prev;
                setInitialForm(JSON.parse(JSON.stringify(merged)));
                return merged;
            });
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || err?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleRefreshCustomer = async () => {
        if (!proformaId) return;
        setRefreshingCustomer(true);
        try {
            const res = await patchProformaInvoice({
                proforma_id: proformaId,
                refresh_customer_from_order: true,
            });
            const row = extractPayload(res) || res?.data;
            toast.success(res?.data?.message || 'Customer details refreshed from order');
            applyServerRow(row);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || err?.message || 'Refresh failed');
        } finally {
            setRefreshingCustomer(false);
        }
    };

    if (loading) {
        return (
            <div className="w-full flex-1 flex flex-col items-center justify-center py-24 gap-3">
                <Spinner className="h-8 w-8 border-purple-500" />
                <p className="text-sm text-gray-500">Loading proforma invoice…</p>
            </div>
        );
    }

    if (loadError || !initialForm) {
        return (
            <div className="w-full flex-1 p-6">
                <Link to="/label-invoice" className="text-sm text-purple-600 hover:underline mb-4 inline-block">
                    ← Back to Label & Invoice
                </Link>
                <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
                    {loadError || 'Unable to load this proforma'}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex-1 pb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                    <Link to="/label-invoice" className="text-sm text-purple-600 hover:underline">
                        ← Back to Label & Invoice
                    </Link>
                    <StyledHeadingText text="Edit Proforma Invoice" TextColor="text-gray-700 font-semibold" />
                    <p className="text-sm text-gray-500 mt-1">
                        <span className="font-medium text-gray-700">{meta.proforma_number || '—'}</span>
                        {meta.order_id ? (
                            <span className="ml-2">· Linked to order</span>
                        ) : (
                            <span className="ml-2">· Standalone</span>
                        )}
                    </p>
                </div>
                {pdfHref && (
                    <a
                        href={pdfHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                    >
                        Open PDF
                    </a>
                )}
            </div>

            <form onSubmit={handleSave} className="space-y-6">
                <div className="bg-white rounded-lg shadow border p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600">Status</label>
                        <select
                            value={form.status}
                            onChange={(e) => handleField('status', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        >
                            <option value="DRAFT">DRAFT</option>
                            <option value="FINAL">FINAL</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600">
                            Proforma date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.proforma_date}
                            onChange={(e) => handleField('proforma_date', e.target.value)}
                            required
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow border p-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Customer</h3>
                        {!isStandalone && (
                            <button
                                type="button"
                                disabled={refreshingCustomer}
                                onClick={handleRefreshCustomer}
                                className="px-3 py-1.5 text-xs font-medium rounded-md bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 disabled:opacity-50"
                            >
                                {refreshingCustomer ? <Spinner className="h-3 w-3 border-amber-700 inline-block mr-1" /> : null}
                                Refresh customer from order
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-gray-600">
                                Customer name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.customer_name}
                                onChange={(e) => handleField('customer_name', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-gray-600">Business name</label>
                            <input
                                type="text"
                                value={form.business_name}
                                onChange={(e) => handleField('business_name', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-gray-600">Phone</label>
                            <input
                                type="text"
                                value={form.customer_phone}
                                onChange={(e) => handleField('customer_phone', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-gray-600">Alternate phone</label>
                            <input
                                type="text"
                                value={form.customer_alternate_phone}
                                onChange={(e) => handleField('customer_alternate_phone', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-gray-600">GST</label>
                            <input
                                type="text"
                                value={form.customer_gst}
                                onChange={(e) => handleField('customer_gst', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="flex flex-col gap-1 md:col-span-2 lg:col-span-3">
                            <label className="text-xs font-medium text-gray-600">Address</label>
                            <textarea
                                value={form.customer_address}
                                onChange={(e) => handleField('customer_address', e.target.value)}
                                rows={3}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                            />
                        </div>
                    </div>
                </div>

                {isStandalone ? (
                    <div className="bg-white rounded-lg shadow border p-5 space-y-4">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Line items</h3>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <span>Tax Mode:</span>
                                    <select
                                        value={form.items[0]?.is_include !== false}
                                        onChange={(e) => {
                                            const isInclude = e.target.value === 'true';
                                            setForm(prev => ({
                                                ...prev,
                                                items: prev.items.map(item => ({ ...item, is_include: isInclude }))
                                            }));
                                        }}
                                        className="px-2 py-1 border border-gray-300 rounded bg-white font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                    >
                                        <option value="true">GST Inclusive</option>
                                        <option value="false">GST Exclusive</option>
                                    </select>
                                </div>
                                <button
                                    type="button"
                                    onClick={addItem}
                                    className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700"
                                >
                                    + Add row
                                </button>
                            </div>
                        </div>
                        <div className="hidden md:grid grid-cols-12 gap-2 px-2 py-2 bg-gray-50 rounded-t text-xs font-semibold text-gray-500 uppercase">
                            <div className="col-span-3">Product</div>
                            <div className="col-span-2">HSN</div>
                            <div className="col-span-1">Qty</div>
                            <div className="col-span-2">Rate ({form.items[0]?.is_include !== false ? 'incl. GST' : 'excl. GST'})</div>
                            <div className="col-span-1">GST %</div>
                            <div className="col-span-2">Taxable / GST / Total</div>
                            <div className="col-span-1 text-center">✕</div>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {form.items.map((item, index) => (
                                <div key={index} className="py-3">
                                    <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                                        <div className="col-span-3">
                                            <ProductSearchInput
                                                value={item.product_name}
                                                placeholder="Product"
                                                onChange={(text) => handleItemChange(index, 'product_name', text)}
                                                onSelect={(s) => handleProductSelect(index, s)}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <input
                                                type="text"
                                                value={item.hsn_code}
                                                onChange={(e) => handleItemChange(index, 'hsn_code', e.target.value)}
                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                                                placeholder="HSN"
                                            />
                                        </div>
                                        <div className="col-span-1">
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={item.rate}
                                                onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                                            />
                                        </div>
                                        <div className="col-span-1">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={item.gst_percent}
                                                onChange={(e) => handleItemChange(index, 'gst_percent', e.target.value)}
                                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
                                            />
                                        </div>
                                        <div className="col-span-2 text-xs text-gray-600">
                                            <div>Tax ₹{calcItemTaxable(item).toFixed(2)}</div>
                                            <div>GST ₹{calcItemGst(item).toFixed(2)}</div>
                                            <div className="font-semibold text-green-700">Total ₹{calcItemTotal(item).toFixed(2)}</div>
                                        </div>
                                        <div className="col-span-1 text-center">
                                            {form.items.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(index)}
                                                    className="text-red-500 hover:text-red-700 font-bold"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="md:hidden space-y-2 p-3 bg-gray-50 rounded-lg border">
                                        <ProductSearchInput
                                            value={item.product_name}
                                            placeholder="Product"
                                            onChange={(text) => handleItemChange(index, 'product_name', text)}
                                            onSelect={(s) => handleProductSelect(index, s)}
                                        />
                                        <div className="grid grid-cols-2 gap-2">
                                            <input
                                                type="text"
                                                value={item.hsn_code}
                                                onChange={(e) => handleItemChange(index, 'hsn_code', e.target.value)}
                                                placeholder="HSN"
                                                className="px-2 py-2 border rounded-lg text-sm"
                                            />
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                className="px-2 py-2 border rounded-lg text-sm"
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={item.rate}
                                                onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                                placeholder={form.items[0]?.is_include !== false ? 'Rate (incl. GST)' : 'Rate (excl. GST)'}
                                                className="px-2 py-2 border rounded-lg text-sm"
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                value={item.gst_percent}
                                                onChange={(e) => handleItemChange(index, 'gst_percent', e.target.value)}
                                                placeholder="GST %"
                                                className="px-2 py-2 border rounded-lg text-sm"
                                            />
                                        </div>
                                        <div className="text-xs text-gray-500 pt-1 border-t flex justify-between">
                                            <span>Taxable: ₹{calcItemTaxable(item).toFixed(2)}</span>
                                            <span>GST: ₹{calcItemGst(item).toFixed(2)}</span>
                                            <span className="font-semibold text-green-700">Total: ₹{calcItemTotal(item).toFixed(2)}</span>
                                        </div>
                                        {form.items.length > 1 && (
                                            <button type="button" onClick={() => removeItem(index)} className="text-red-600 text-xs">
                                                Remove row
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="pt-3 border-t flex flex-col sm:flex-row sm:items-center gap-2">
                            <label className="text-sm font-medium text-gray-600">Discount (₹)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.discount_amount}
                                onChange={(e) => handleField('discount_amount', e.target.value)}
                                className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm text-amber-900">
                            Line items are taken from the linked order. Edit the order to change products or quantities; use{' '}
                            <strong>Refresh customer from order</strong> above to pull the latest billing details from the order.
                        </p>
                        {Array.isArray(form.items) && form.items.length > 0 && (
                            <div className="overflow-x-auto bg-white rounded border border-amber-100">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 text-left text-gray-600">
                                            <th className="px-3 py-2">Product</th>
                                            <th className="px-3 py-2">HSN</th>
                                            <th className="px-3 py-2 text-right">Qty</th>
                                            <th className="px-3 py-2 text-right">Rate</th>
                                            <th className="px-3 py-2 text-right">GST %</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {form.items.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="px-3 py-2">{item.product_name || '—'}</td>
                                                <td className="px-3 py-2">{item.hsn_code || '—'}</td>
                                                <td className="px-3 py-2 text-right">{item.quantity}</td>
                                                <td className="px-3 py-2 text-right">{item.rate}</td>
                                                <td className="px-3 py-2 text-right">{item.gst_percent}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-wrap gap-3">
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-8 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving ? <Spinner /> : null}
                        Save changes
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/label-invoice')}
                        className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                </div>
            </form>

            {pdfHref && (
                <div className="mt-8 rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-2 bg-white border-b">PDF preview</p>
                    <iframe title="Proforma PDF" src={pdfHref} className="w-full h-[720px] bg-white" />
                </div>
            )}
        </div>
    );
}

export default ProformaInvoiceEdit;
